<!-- align-stretch (without the contradictory align-center) + display-flex
on each column equalize card heights, so the bottom-pinned CTAs land
at the same Y. -->
<template>
  <div class="row roomy align-stretch">
    <div
      v-for="(sku, index) in filteredSkus"
      :key="sku"
      :class="['column-12 size-sm display-flex', columnSpanClass]"
    >
      <plan-summary-card
        v-if="!isLoading"
        :is-last="filteredSkus.length === index + 1"
        :is-single-plan-modal="filteredSkus.length === 1"
        :modal-context-slug="upsellTriggerSlug"
        :sku="getPlan(sku).sku"
        is-in-modal-context
        suppress-featured
        :subscription-discounts="subscriptionDiscounts"
      />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { getAutoAppliedDiscounts, getKickstarterDiscounts } from 'app/views/plans/PlansData'
  import PlanSummaryCard from 'app/views/plans/components/PlanSummaryCard'

  export default {
    name: 'UpsellOptions',

    components: {
      PlanSummaryCard
    },

    props: {
      icon: {
        type: Object,
        default: null
      },
      isCustomIcon: {
        type: Boolean,
        default: false
      },
      upsellTriggerSlug: {
        type: String,
        required: true
      },
      // The policy's chosen target SKU(s). Drives plan-card filtering.
      overrideTargetSkus: {
        type: Array,
        default: null
      },
      // The customer's own plan, rendered ahead of the targets purely as
      // context — deliberately not part of `overrideTargetSkus`, which must
      // never contain the plan the customer already holds.
      contextPlanSku: {
        type: String,
        default: null
      }
    },

    emits: ['onPlansReady', 'onSkuCount'],

    data() {
      return {
        isLoading: true
      }
    },

    computed: {
      ...mapGetters('plans', ['getPlan', 'subscriptionDiscounts']),
      ...mapGetters('discounts', ['globalDiscountCode']),

      filteredSkus() {
        const normalizeSku = (rawSku) => {
          const sku = rawSku && typeof rawSku === 'object' && rawSku.sku ? rawSku.sku : rawSku
          return String(sku || '').toUpperCase()
        }

        if (!this.overrideTargetSkus || this.overrideTargetSkus.length === 0) {
          return []
        }

        const targets = this.overrideTargetSkus.map(normalizeSku)
        // Current plan first, so the card order reads as before-then-after.
        const withContext = this.contextPlanSku ? [normalizeSku(this.contextPlanSku), ...targets] : targets

        return [...new Set(withContext)].slice(0, 3)
      },

      // Single plan gets the full width; two split 50/50; three each take a
      // third. Falls through to full width on mobile (column-12 always set).
      columnSpanClass() {
        if (this.filteredSkus.length === 1) return 'laptop:column-12'
        if (this.filteredSkus.length === 2) return 'laptop:column-6'
        return 'laptop:column-4'
      },

      isBackerWithoutSubscription() {
        if (!this.$account) {
          return false
        }

        return this.$account.isBacker && !this.$subscription
      }
    },

    watch: {
      filteredSkus: {
        immediate: true,
        handler(val) {
          const count = Array.isArray(val) ? val.length : 0
          this.$emit('onSkuCount', count)
        }
      }
    },

    async created() {
      if (!this.plansLoaded) {
        await this.fetchPlans()
      }

      // Fetch discounts before signalling readiness: the plan cards depend on
      // discounted pricing, so resolving them here lets the whole modal reveal
      // at once instead of showing the header first and popping the plan cards
      // in a beat later once discounts land. A discount failure must not trap
      // the modal in its loading state — fall through and render undiscounted.
      try {
        if (this.globalDiscountCode) {
          await this.fetchDiscounts([this.globalDiscountCode])
        } else if (getKickstarterDiscounts().length > 0 && this.isBackerWithoutSubscription) {
          await this.fetchDiscounts(getKickstarterDiscounts())
        } else if (getAutoAppliedDiscounts().length > 0) {
          await this.fetchDiscounts(getAutoAppliedDiscounts())
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[UpsellOptions] discount fetch failed; showing undiscounted plans', e)
      }

      this.isLoading = false
      this.$emit('onPlansReady', true)
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts'])
    }
  }
</script>
